<template>
  <Dialog modal dismissableMask position="top" header="Bin groups"
    v-model:visible="showingDialog"
    @update:visible="$emit('close')"
    @keydown="onDialogKeydown"
  >
    <div style="display: flex; gap: 20px; margin-top: -20px;">
      <div style="display: flex; flex-direction: column; gap: 5px; align-items: stretch; flex-grow: 1;">
        <Listbox v-if="binGroups.length > 0"
          :modelValue="selectedBinGroup"
          :options="binGroups"
          :optionLabel="'groupName'"
          @update:modelValue="onSelectBinGroup"
          style="width: 200px; min-height: 300px"
        ></Listbox>
        <Button severity="secondary" @click="createNewGroup()">
          Create new group
        </Button>
      </div>
      <div v-if="selectedBinGroup" style="display: flex; flex-direction: column; gap: 20px;">
        <div style="display: flex; flex-direction: column; gap: 5px;">
          <label style="font-weight: bold; display: flex; align-items: center; justify-content: space-between;">
            Group name
            <Button size="small" severity="secondary" @click="deleteGroup(selectedBinGroup)">
              Delete group
            </Button>
          </label>
          <InputText ref="groupNameInputRef" v-model="selectedBinGroup.groupName" @input="onInputGroupName()"></InputText>
        </div>
        <div style="display: flex; flex-direction: column; gap: 10px;">
          <label style="font-weight: bold; display: flex; align-items: center; justify-content: space-between;">
            Included bins
          </label>
          <div style="display: flex; gap: 30px">
            <div v-for="system in systems" style="padding-left: 0px;">
              <div style="display: flex; flex-direction: column; gap: 3px;">
                <Checkbox v-if="systems.length > 1" style="font-weight: 600;"
                  :modelValue="areAllSystemBinsSelected(system)"
                  @change="toggleSystemBins(system)"
                >
                  {{ system.boardName.value }}
                </Checkbox>
                <template v-for="bin in system.bins.children">
                  <Checkbox v-model="selectedBinGroup.bins" :value="bin" :binary="false">
                    {{ bin.name.value }}
                  </Checkbox>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <Button outlined severity="primary" style="flex: 1 0px;" @click="submit()">
        Ok
      </Button>
      <Button outlined severity="danger" style="flex: 1 0px;" @click="cancel()">
        Cancel
      </Button>
    </template>
  </Dialog>
</template>

<script setup>
import { computed, ref } from "vue";
import { promiseTimeout, whenever } from "@vueuse/core";
import { unique } from "./utility.js";

let props = defineProps([
  "showingDialog"
]);

let showingDialog = defineModel("showingDialog");

let sessions = computed(() => {
  return store.sessions.filter(session => session.system != null);
});

let systems = computed(() => {
  return sessions.value.map(session => session.system);
});

let binGroups = ref(null);
let selectedBinGroup = ref(null);
whenever(showingDialog, () => {
  binGroups.value = createInitialBinGroups();
  selectedBinGroup.value = binGroups.value[0];
});

function createInitialBinGroups() {
  let allBins = systems.value.flatMap(system => system.bins.children);
  let groupNames = unique(allBins.flatMap(bin => bin.binGroups.value));
  groupNames.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
  let groups = groupNames.map(groupName => {
    return {
      key: Math.random(),
      groupName,
      bins: allBins.filter(bin => bin.binGroups.value.includes(groupName))
    };
  });
  return groups;
};

let groupNameInputRef = ref();
async function createNewGroup() {
  let newGroup = { key: Math.random(), groupName: "New group", bins: [] };
  binGroups.value.push(newGroup);
  selectedBinGroup.value = newGroup;
  await promiseTimeout(1);
  groupNameInputRef.value.$el.select();
}

function deleteGroup(group) {
  let selectedIndex = binGroups.value.indexOf(group);
  binGroups.value.splice(selectedIndex, 1);
  selectedBinGroup.value = (
    binGroups.value[selectedIndex] ??
    binGroups.value[selectedIndex - 1] ??
    null
  );
}

function onSelectBinGroup(group) {
  if (group != null)
    selectedBinGroup.value = group;
}

function onInputGroupName() {
  let matchingSystem = systems.value.find(system => system.boardName.value == selectedBinGroup.value.groupName);
  if (matchingSystem) {
    selectedBinGroup.value.bins = [];
    toggleSystemBins(matchingSystem);
  }
}

function areAllSystemBinsSelected(system) {
  return system.bins.children.every(bin => selectedBinGroup.value.bins.includes(bin));
}

function toggleSystemBins(system) {
  if (areAllSystemBinsSelected(system)) {
    selectedBinGroup.value.bins = selectedBinGroup.value.bins.filter(bin => !system.bins.children.includes(bin));
  }
  else {
    system.bins.children.forEach(bin => {
      if (!selectedBinGroup.value.bins.includes(bin))
        selectedBinGroup.value.bins.push(bin);
    });
  }
}

function onDialogKeydown(event) {
  // If this dialog was opened from a <KeypadSetting> or any other component
  // that accepts keyboard input then we need to prevent input on this dialog
  // from reaching those components.
  event.stopPropagation();
}

function submit() {
  sessions.value.forEach(session => {
    let settingsData = session.system.bins.children.map(bin => {
      let groups = binGroups.value.filter(group => group.bins.includes(bin));
      let groupNames = groups.map(group => group.groupName);
      return { handle: bin.binGroups.handle, value: groupNames };
    });
    session.changeSettings(settingsData);
  });
  showingDialog.value = false;
}

function cancel() {
  showingDialog.value = false;
}
</script>

<style scoped>
.binSelection .p-button {
  padding-left: 12px;
  padding-right: 12px;
}
.binSelection .p-dropdown-label.p-inputtext {
  padding-right: 0px;
  padding-top: 6px;
  padding-bottom: 5px;
  font-size: small;
}
</style>